import * as React from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    errorText,
    getShabbatHosts,
    inviteShabbatHost,
    shabbatHostPasswordLink,
    ShabbatHost,
    ShabbatHostInvite,
} from '../../services/shabbat/shabbatService.ts';

/**
 * Admins only, on a venue that hosts Shabbat meals: the venue's own login, so
 * the place publishes its meals itself (Ariel, 23 Oct 2026: "each place gives
 * its own meals - not admin creating them").
 *
 * No login yet: email + name (+ phone) makes one - a restaurant login tied to
 * this venue only; for a Shabbat-only venue (a Chabad House) the panel shows it
 * just the Shabbat pages. The login email goes out, and the admin also gets a
 * one-time link where the host chooses a password, to send by WhatsApp when the
 * email does not arrive. A login exists: who it is, and a fresh link on demand.
 */
export default function ShabbatHostLogin({ locationId, shabbatOnly }: { locationId: number; shabbatOnly: boolean }) {
    const [hosts, setHosts] = React.useState<ShabbatHost[] | null>(null);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [email, setEmail] = React.useState('');
    const [name, setName] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [invite, setInvite] = React.useState<ShabbatHostInvite | null>(null);
    const [copied, setCopied] = React.useState(false);

    const load = React.useCallback(async () => {
        try {
            setHosts(await getShabbatHosts(locationId));
            setLoadError(null);
        } catch (e: any) {
            setLoadError(errorText(e, 'Could not load the venue login'));
        }
    }, [locationId]);

    React.useEffect(() => { load(); }, [load]);

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const phoneOk = phone.trim() === '' || /^\+[1-9]\d{6,14}$/.test(phone.trim());
    const canCreate = emailOk && name.trim().length > 1 && phoneOk && !busy;

    const create = async () => {
        if (!canCreate) return;
        setBusy(true);
        setError(null);
        try {
            const made = await inviteShabbatHost(locationId, {
                email: email.trim(),
                name: name.trim(),
                ...(phone.trim() ? { phoneNumber: phone.trim() } : {}),
            });
            setInvite(made);
            setEmail(''); setName(''); setPhone('');
            await load();
        } catch (e: any) {
            setError(errorText(e, 'Could not create the login'));
        } finally {
            setBusy(false);
        }
    };

    const freshLink = async (host: ShabbatHost) => {
        setBusy(true);
        setError(null);
        try {
            setInvite(await shabbatHostPasswordLink(locationId, host.id));
        } catch (e: any) {
            setError(errorText(e, 'Could not make a new link'));
        } finally {
            setBusy(false);
        }
    };

    const copy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* the link stays selectable */ }
    };

    const noEnter = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') { e.preventDefault(); create(); }
    };

    return (
        <Box
            data-testid="shabbat-host-login"
            sx={{ border: '1px solid var(--color-secondary)', borderRadius: 'var(--radius-6, 6px)', p: 3, mt: 3, maxWidth: 760 }}
        >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Venue login</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {shabbatOnly
                    ? 'The host (the rabbi) signs in to this panel with this login and publishes this venue\'s Shabbat meals. It sees only this venue\'s Shabbat pages.'
                    : 'The restaurant signs in with this login and publishes its own Shabbat meals, next to its orders and menu. It sees only this venue.'}
            </Typography>

            {hosts === null && !loadError && <CircularProgress size={22} />}
            {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

            {hosts && hosts.length > 0 && hosts.map((host) => (
                <Box key={host.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 240 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{host.email}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {[host.name, host.phoneNumber, host.blocked ? 'blocked' : null].filter(Boolean).join(' · ')}
                        </Typography>
                    </Box>
                    <Button type="button" variant="outlined" disabled={busy} onClick={() => freshLink(host)}>
                        New set-password link
                    </Button>
                </Box>
            ))}

            {hosts && hosts.length === 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr 1fr auto' }, gap: 1.5, alignItems: 'start' }}>
                    <TextField
                        size="small" label="Email" type="email" value={email}
                        onChange={(e) => setEmail(e.target.value)} onKeyDown={noEnter}
                        error={email.trim() !== '' && !emailOk}
                        slotProps={{ htmlInput: { 'aria-label': 'Host email' } }}
                    />
                    <TextField
                        size="small" label="Name" value={name}
                        onChange={(e) => setName(e.target.value)} onKeyDown={noEnter}
                        slotProps={{ htmlInput: { 'aria-label': 'Host name' } }}
                    />
                    <TextField
                        size="small" label="Phone (optional)" placeholder="+390200000000" value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\s/g, ''))} onKeyDown={noEnter}
                        error={!phoneOk}
                        helperText={!phoneOk ? 'International format, e.g. +39...' : ' '}
                        slotProps={{ htmlInput: { 'aria-label': 'Host phone' } }}
                    />
                    <Button
                        type="button" variant="contained" disabled={!canCreate} onClick={create}
                        sx={{ borderRadius: 0, backgroundColor: 'var(--color-primary)', boxShadow: 'none', height: 40 }}
                    >
                        {busy ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Create login'}
                    </Button>
                </Box>
            )}

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            {invite && (
                <Alert severity={invite.emailSent || !invite.setPasswordLink ? 'success' : 'warning'} sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: invite.setPasswordLink ? 1 : 0 }}>
                        {invite.emailSent
                            ? `Login created. The sign-in email went to ${invite.host.email}.`
                            : invite.setPasswordLink
                                ? `Send ${invite.host.email} this link to choose a password (it works once):`
                                : `Login ${invite.host.email}: no link could be made.`}
                        {invite.emailSent && invite.setPasswordLink ? ' If it does not arrive, send this one-time link to choose a password:' : ''}
                    </Typography>
                    {invite.setPasswordLink && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                size="small" fullWidth value={invite.setPasswordLink}
                                slotProps={{ htmlInput: { readOnly: true, 'aria-label': 'Set-password link' } }}
                                onFocus={(e) => e.target.select()}
                            />
                            <Tooltip title={copied ? 'Copied' : 'Copy'}>
                                <IconButton type="button" onClick={() => copy(invite.setPasswordLink as string)} aria-label="Copy link">
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Alert>
            )}
        </Box>
    );
}
